/**
 * Dashboard Page
 */

import * as React from "react"
import { useNavigate } from "react-router"
import { useProjects } from "@/hooks/use-projects"
import { useRecentActivity, type ActivityItem } from "@/hooks/use-activity"
import { ProjectCard } from "@/components/projects/project-card"
import { CreateProjectDialog } from "@/components/projects/create-project-dialog"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/hooks/use-auth"
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  Target,
  User,
  Compass,
  BookOpen,
  FileText,
  History,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const PROJECT_TYPES = [
  { value: "all", label: "All Types" },
  { value: "enterprise", label: "Enterprise" },
  { value: "startup", label: "Startup" },
  { value: "saas", label: "SaaS" },
  { value: "internal", label: "Internal" },
]

const ITEMS_PER_PAGE = 6

const activityIcons: Record<string, React.ElementType> = {
  goal: Target,
  persona: User,
  journey: Compass,
  story: BookOpen,
  document: FileText,
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: projects, isLoading, error } = useProjects()
  const { data: activities } = useRecentActivity()

  // Filter and pagination state
  const [searchQuery, setSearchQuery] = React.useState("")
  const [typeFilter, setTypeFilter] = React.useState("all")
  const [currentPage, setCurrentPage] = React.useState(1)

  // Compute dashboard stats from projects
  const totalArtifacts = React.useMemo(() => {
    if (!projects) return 0
    return projects.reduce((sum, p) => sum + p.artefacts.length, 0)
  }, [projects])

  // Filter projects
  const filteredProjects = React.useMemo(() => {
    if (!projects) return []

    return projects.filter((project) => {
      const matchesSearch = !searchQuery ||
        project.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesType = typeFilter === "all" || project.type === typeFilter
      return matchesSearch && matchesType
    })
  }, [projects, searchQuery, typeFilter])

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE)
  const paginatedProjects = React.useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredProjects.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredProjects, currentPage])

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, typeFilter])

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
  }

  return (
    <div className="flex-1 flex flex-col pb-8 md:pb-12">
      <div className="relative mb-7 overflow-hidden rounded-[28px] border border-[#2f2b3f]/20 bg-[linear-gradient(120deg,#050019_0%,#25115b_55%,#7c4af7_100%)] px-5 py-6 text-white shadow-card md:mb-8 md:px-8 md:py-8">
        <div className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-8 -bottom-16 h-40 w-40 rounded-full bg-brand-200/35 blur-3xl" />
        <p className="wm-kicker !text-brand-100">Workspace Overview</p>
        <h1 className="mt-2 text-2xl font-semibold md:text-3xl">
          Welcome back, {user?.name?.split(" ")[0] || "User"}
        </h1>
        <p className="mt-2 max-w-[620px] text-sm text-white/80 md:text-base">
          Your AI-powered product development workspace. Continue active initiatives, launch new projects, and accelerate delivery.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5">Projects: {projects?.length ?? 0}</span>
          <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5">Artifact types: {totalArtifacts}</span>
        </div>
      </div>

      <section className="wm-panel mb-6 rounded-3xl p-4 md:mb-8 md:p-6">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-display text-lg font-semibold text-navy">
            Your Projects
            {projects && projects.length > 0 && (
              <span className="ml-2 text-sm font-medium text-gray-500">({filteredProjects.length})</span>
            )}
          </h2>

          {projects && projects.length > 0 && (
            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full rounded-full border border-[#d8d1eb] bg-white/90 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary sm:w-[170px] md:w-[220px]"
                />
              </div>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-10 w-[120px] rounded-full border-[#d8d1eb] bg-white/90 md:w-[150px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <Skeleton className="h-[338px] w-full rounded-[18px]" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="py-10 text-center">
            <p className="text-red-600">Failed to load projects</p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        ) : !projects || projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-brand-300/70 bg-white/75 px-8 py-16">
            <div className="relative mb-6">
              <div className="absolute inset-0 scale-150 rounded-full bg-brand-primary/15 blur-xl" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-brand-primary/25 bg-gradient-to-br from-brand-primary/25 to-brand-primary/5">
                <FolderIcon className="h-10 w-10 text-brand-primary" />
              </div>
            </div>

            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              No projects yet
            </h3>
            <p className="mb-6 max-w-sm text-center text-gray-500">
              Create your first project to start organizing your work and collaborating with your team
            </p>

            <CreateProjectDialog
              trigger={
                <Button className="h-11 rounded-xl px-6">
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Create Project</span>
                </Button>
              }
            />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#d8d1eb] bg-white/75 px-8 py-16">
            <div className="relative mb-6">
              <div className="absolute inset-0 scale-150 rounded-full bg-gray-200/55 blur-xl" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-100 to-white">
                <Search className="h-10 w-10 text-gray-400" />
              </div>
            </div>

            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              No matching projects
            </h3>
            <p className="mb-6 max-w-sm text-center text-gray-500">
              Try adjusting your search or filter criteria to find what you're looking for
            </p>

            <Button
              variant="outline"
              className="h-11 rounded-xl border-2 px-6 hover:bg-white transition-all"
              onClick={() => {
                setSearchQuery("")
                setTypeFilter("all")
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => navigate(`/projects/${project.id}`)}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex flex-col items-start justify-between gap-4 border-t border-[#d8d1eb] pt-6 lg:flex-row lg:items-center">
                <p className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredProjects.length)} of {filteredProjects.length} projects
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="h-9 px-3"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={cn(
                          "h-9 w-9 rounded-full text-sm font-medium transition-colors",
                          page === currentPage
                            ? "bg-navy text-white shadow-soft-lg"
                            : "text-gray-700 hover:bg-white"
                        )}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="h-9 px-3"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Recent Activity */}
      {activities && activities.length > 0 && (
        <section className="wm-panel mt-auto rounded-3xl p-4 pt-6 md:p-6">
          <div className="mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-gray-500" />
            <h2 className="font-display text-lg font-semibold text-navy">
              Recent Activity
            </h2>
          </div>
          <div className="divide-y divide-[#e6e2f2]">
            {activities.map((item: ActivityItem) => {
              const Icon = activityIcons[item.type] || FileText
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => navigate(`/projects/${item.projectId}`)}
                  className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-[#f8f6ff] first:pt-0 last:pb-0 rounded-lg px-2 -mx-2"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#f1eef8]">
                    <Icon className="h-4 w-4 text-[#7c4af7]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
                    <p className="truncate text-xs text-gray-500">
                      {item.projectEmoji} {item.projectName}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-400">{timeAgo(item.updatedAt)}</span>
                </button>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}

function FolderIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    </svg>
  )
}
